/**
 * Customer Onboarding Form Page
 * Public page accessible via invitation token
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/shared/Toast';
import { MdArrowBack } from 'react-icons/md';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

interface FormData {
  companyName: string;
  businessGroupName: string;
  companyActivity: string;
  contactPerson: string;
  email: string;
  mobileCountryCode: string;
  mobilePhone: string;
  deskCountryCode: string;
  deskPhone: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  vessels: number;
  taxId: string;
  accountName: string;
  bankName: string;
  iban: string;
  swift: string;
  invoiceEmail: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingProvince: string;
  billingPostal: string;
  billingCountry: string;
}

export function CustomerOnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    businessGroupName: '',
    companyActivity: '',
    contactPerson: '',
    email: '',
    mobileCountryCode: '+31',
    mobilePhone: '',
    deskCountryCode: '+31',
    deskPhone: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    vessels: 1,
    taxId: '',
    accountName: '',
    bankName: '',
    iban: '',
    swift: '',
    invoiceEmail: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingProvince: '',
    billingPostal: '',
    billingCountry: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid invitation link');
      return;
    }

    // Fetch invitation data
    fetch(`${API_URL}/api/v1/onboarding/invitation?token=${token}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setInvitationData(data.data);
          setFormData((prev) => ({
            ...prev,
            email: data.data.email,
          }));
        } else {
          toast.error(data.error || 'Invalid invitation token');
        }
      })
      .catch((error) => {
        toast.error('Failed to load invitation');
        console.error(error);
      });
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.mobilePhone.trim()) newErrors.mobilePhone = 'Mobile phone is required';
    if (!formData.deskPhone.trim()) newErrors.deskPhone = 'Desk phone is required';
    if (!formData.address1.trim()) newErrors.address1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.province.trim()) newErrors.province = 'Province is required';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (formData.vessels < 1) newErrors.vessels = 'At least 1 vessel is required';
    if (!formData.taxId.trim()) newErrors.taxId = 'Tax ID is required';
    if (!formData.accountName.trim()) newErrors.accountName = 'Account name is required';
    if (!formData.bankName.trim()) newErrors.bankName = 'Bank name is required';
    if (!formData.iban.trim()) newErrors.iban = 'IBAN is required';
    if (!formData.invoiceEmail.trim()) newErrors.invoiceEmail = 'Invoice email is required';
    if (!formData.billingAddress1.trim()) newErrors.billingAddress1 = 'Billing address is required';
    if (!formData.billingCity.trim()) newErrors.billingCity = 'Billing city is required';
    if (!formData.billingProvince.trim()) newErrors.billingProvince = 'Billing province is required';
    if (!formData.billingPostal.trim()) newErrors.billingPostal = 'Billing postal code is required';
    if (!formData.billingCountry.trim()) newErrors.billingCountry = 'Billing country is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    if (!token) {
      toast.error('Invalid invitation token');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/v1/onboarding/customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          ...formData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Onboarding completed successfully!');
        // Redirect to success page or login
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(data.error || 'Failed to submit onboarding');
      }
    } catch (error: any) {
      toast.error('Failed to submit onboarding');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0088cc]/5 dark:bg-[#0088cc]/25">
        <div className="text-center">
          <p className="text-[hsl(var(--destructive))]">Invalid invitation link</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0088cc]/5 dark:bg-[#0088cc]/25 py-8">
      <div className="w-full px-4">
        <div className="bg-[hsl(var(--card))] rounded-lg shadow-lg p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-2">
              Customer Onboarding – Euroasiann Marine Spares
            </h1>
            <p className="text-[hsl(var(--muted-foreground))]">
              Complete your organization onboarding to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Company Details */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Company Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Shipping Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.companyName ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Business Group Name
                  </label>
                  <input
                    type="text"
                    name="businessGroupName"
                    value={formData.businessGroupName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Company Activity
                  </label>
                  <input
                    type="text"
                    name="companyActivity"
                    value={formData.companyActivity}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Primary Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.contactPerson ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.contactPerson && <p className="mt-1 text-sm text-red-600">{errors.contactPerson}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Official Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-[#0088cc]/10 dark:bg-[#0088cc]/40 text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Number of Vessels to Onboard <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="vessels"
                    value={formData.vessels}
                    onChange={handleChange}
                    min="1"
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.vessels ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.vessels && <p className="mt-1 text-sm text-red-600">{errors.vessels}</p>}
                </div>
              </div>
            </div>

            {/* Phone Numbers */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Phone Numbers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Mobile Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="mobileCountryCode"
                      value={formData.mobileCountryCode}
                      onChange={handleChange}
                      className="w-32 px-3 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                    >
                      <option value="+31">🇳🇱 +31 (NL)</option>
                      <option value="+91">🇮🇳 +91 (IN)</option>
                      <option value="+1">🇺🇸 +1 (US)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+65">🇸🇬 +65 (SG)</option>
                    </select>
                    <input
                      type="tel"
                      name="mobilePhone"
                      value={formData.mobilePhone}
                      onChange={handleChange}
                      className={`flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                        errors.mobilePhone ? 'border-red-500' : 'border-[hsl(var(--border))]'
                      }`}
                      required
                    />
                  </div>
                  {errors.mobilePhone && <p className="mt-1 text-sm text-red-600">{errors.mobilePhone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Desk Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="deskCountryCode"
                      value={formData.deskCountryCode}
                      onChange={handleChange}
                      className="w-32 px-3 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                    >
                      <option value="+31">🇳🇱 +31 (NL)</option>
                      <option value="+91">🇮🇳 +91 (IN)</option>
                      <option value="+1">🇺🇸 +1 (US)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+65">🇸🇬 +65 (SG)</option>
                    </select>
                    <input
                      type="tel"
                      name="deskPhone"
                      value={formData.deskPhone}
                      onChange={handleChange}
                      className={`flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                        errors.deskPhone ? 'border-red-500' : 'border-[hsl(var(--border))]'
                      }`}
                      required
                    />
                  </div>
                  {errors.deskPhone && <p className="mt-1 text-sm text-red-600">{errors.deskPhone}</p>}
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Address
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address1"
                    value={formData.address1}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.address1 ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.address1 && <p className="mt-1 text-sm text-red-600">{errors.address1}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    name="address2"
                    value={formData.address2}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.city ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Province / State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.province ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.province && <p className="mt-1 text-sm text-red-600">{errors.province}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.postalCode ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.postalCode && <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.country ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  >
                    <option value="">-- Select Country --</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="India">India</option>
                    <option value="United States">USA</option>
                    <option value="United Kingdom">UK</option>
                    <option value="Singapore">Singapore</option>
                    <option value="Germany">Germany</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Tax Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Tax ID / VAT / GST / EIN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.taxId ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.taxId && <p className="mt-1 text-sm text-red-600">{errors.taxId}</p>}
                </div>
              </div>
            </div>

            {/* Banking Details */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Banking Details (For Auto-Debit)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Account Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.accountName ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.accountName && <p className="mt-1 text-sm text-red-600">{errors.accountName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.bankName ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.bankName && <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    IBAN / Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.iban ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.iban && <p className="mt-1 text-sm text-red-600">{errors.iban}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    SWIFT / BIC Code
                  </label>
                  <input
                    type="text"
                    name="swift"
                    value={formData.swift}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                  />
                </div>
              </div>
            </div>

            {/* Invoicing Details */}
            <div>
              <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-4 pb-2 border-b border-[hsl(var(--border))]">
                Invoicing Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Email for Invoicing <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="invoiceEmail"
                    value={formData.invoiceEmail}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.invoiceEmail ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.invoiceEmail && <p className="mt-1 text-sm text-red-600">{errors.invoiceEmail}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="billingAddress1"
                    value={formData.billingAddress1}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.billingAddress1 ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.billingAddress1 && <p className="mt-1 text-sm text-red-600">{errors.billingAddress1}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing Address Line 2
                  </label>
                  <input
                    type="text"
                    name="billingAddress2"
                    value={formData.billingAddress2}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] border-[hsl(var(--border))]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="billingCity"
                    value={formData.billingCity}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.billingCity ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.billingCity && <p className="mt-1 text-sm text-red-600">{errors.billingCity}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing Province / State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="billingProvince"
                    value={formData.billingProvince}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.billingProvince ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.billingProvince && <p className="mt-1 text-sm text-red-600">{errors.billingProvince}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="billingPostal"
                    value={formData.billingPostal}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.billingPostal ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  />
                  {errors.billingPostal && <p className="mt-1 text-sm text-red-600">{errors.billingPostal}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Billing Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="billingCountry"
                    value={formData.billingCountry}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] ${
                      errors.billingCountry ? 'border-red-500' : 'border-[hsl(var(--border))]'
                    }`}
                    required
                  >
                    <option value="">-- Select Country --</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="India">India</option>
                    <option value="USA">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="Germany">Germany</option>
                    <option value="Singapore">Singapore</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.billingCountry && <p className="mt-1 text-sm text-red-600">{errors.billingCountry}</p>}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-6 py-2 border border-[hsl(var(--border))] rounded-lg text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting...' : 'Proceed to Pricing'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

